'use client';

import { useAtomSet, useAtomValue } from '@effect/atom-react';
import {
  SortStrip,
  SortStripOption,
  SortStripOptionButton,
  SortStripOptions,
  SortStripTotal,
} from '@vs-shop/commerce-ui/sortStrip';
import type { ListingScopeKey } from '@vs-shop/engine/capability/productListing/listingScope';
import type { ListingSort as ListingSortValue } from '@vs-shop/engine/capability/productListing/listingSelection';
import { Predicate } from 'effect';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useIntl } from 'react-intl';

import { ProductsOrderBy, intlProductsOrderBy } from '../../../enum/productsOrderBy';
import { rcprofiListingJourney } from '../../../effect/listing/listingJourney';

type Props = { readonly scopeKey: ListingScopeKey };

const sameSort = (left: ListingSortValue, right: ListingSortValue): boolean =>
  left.field === right.field && left.direction === right.direction;

export const ListingSort = ({ scopeKey }: Props) => {
  const intl = useIntl();
  const label = intlProductsOrderBy(intl);
  const binding = rcprofiListingJourney.bind(scopeKey);
  const state = useAtomValue(binding.state);
  const current = useAtomValue(binding.current);
  const change = useAtomSet(binding.change, { mode: 'value' });
  const defaultOrder = Predicate.isTagged(state.scope, 'Query')
    ? ProductsOrderBy.RELEVANCE
    : ProductsOrderBy.BESTSELLERS;
  const options: ReadonlyArray<{ sort: ListingSortValue; label: string; orderBy: string }> = [
    {
      sort: { field: 'none', direction: 'asc' },
      label: label(defaultOrder),
      orderBy: defaultOrder,
    },
    {
      sort: { field: 'created', direction: 'desc' },
      label: label(ProductsOrderBy.CREATED_AT_ASC),
      orderBy: ProductsOrderBy.CREATED_AT_ASC,
    },
    {
      sort: { field: 'price', direction: 'asc' },
      label: label(ProductsOrderBy.PRICE_ASC),
      orderBy: ProductsOrderBy.PRICE_ASC,
    },
    {
      sort: { field: 'price', direction: 'desc' },
      label: label(ProductsOrderBy.PRICE_DESC),
      orderBy: ProductsOrderBy.PRICE_DESC,
    },
  ];
  const total = AsyncResult.match(current, {
    onInitial: () => null,
    onFailure: () => null,
    onSuccess: ({ value: { products } }) => products.total,
  });

  return (
    <SortStrip className="mb-6 lap:mb-8">
      <SortStripOptions>
        {options.map((option) => (
          <SortStripOption key={`${option.sort.field}-${option.sort.direction}`}>
            <SortStripOptionButton
              data-order-by={option.orderBy}
              aria-pressed={sameSort(state.selection.sort, option.sort)}
              onClick={() => change({ _tag: 'SetSort', sort: option.sort })}
            >
              {option.label}
            </SortStripOptionButton>
          </SortStripOption>
        ))}
      </SortStripOptions>
      <SortStripTotal>
        {total === null
          ? null
          : intl.formatMessage(
              {
                id: 'category.products.sort.total',
                defaultMessage: 'Zobrazuji {total} produktů',
              },
              { total: intl.formatNumber(total) }
            )}
      </SortStripTotal>
    </SortStrip>
  );
};
